import React, { useEffect, useRef, useState } from "react";
import EmojiPicker from "emoji-picker-react";
import {
  AttachFile,
  Send,
  Close,
  Image,
  VideoCall,
  Description,
} from "@mui/icons-material";
import MicIcon from "@mui/icons-material/Mic";
import VoiceRecorder from "./VoiceRecorder";

export default function MessageInput({
  input,
  inputRef,
  sending,
  connectionStatus,
  showEmoji,
  replyingTo,
  onInputChange,
  members = [],
  onKeyPress,
  onSend,
  onEmojiToggle,
  onSetInput,
  onCancelReply,
  disabled = false,
  otherUser = null,
  pastedImages = [], // ✅ NEW PROP
  onRemovePastedImage = () => { }, // ✅ NEW PROP
}) {
  const pickerRef = useRef(null);
  const [mentionList, setMentionList] = useState([]);
  const [mentionIndex, setMentionIndex] = useState(-1);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const BASE_DOWNLOAD_URL = process.env.REACT_APP_API_BASE_URL_DOWNLOAD;

  const isOtherUserInactive = otherUser && !otherUser.isActive;
  const isInputDisabled = disabled || isOtherUserInactive;

  // ✅ Create preview URLs for pasted images
  const [pastedImagePreviews, setPastedImagePreviews] = useState([]);

  useEffect(() => {
    // Generate preview URLs for pasted images
    const previews = pastedImages.map(file => URL.createObjectURL(file));
    setPastedImagePreviews(previews);

    // Cleanup URLs when component unmounts or images change
    return () => {
      previews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [pastedImages]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        onEmojiToggle();
      }
    };

    if (showEmoji) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmoji, onEmojiToggle]);

  const handleInput = (e) => {
    if (isInputDisabled) return;

    const value = e.target.value;
    onInputChange(e);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);
    const match = textBeforeCursor.match(/@([\w]*)$/);

    if (match) {
      const query = match[1].toLowerCase();
      const filtered = members.filter((m) =>
        m.username.toLowerCase().startsWith(query)
      );
      setMentionList(filtered);
      setMentionIndex(filtered.length > 0 ? 0 : -1);
    } else {
      setMentionList([]);
      setMentionIndex(-1);
    }
  };

  const insertMention = (user) => {
    if (isInputDisabled) return;

    const cursorPos = inputRef.current.selectionStart;
    const textBefore = input.slice(0, cursorPos).replace(/@[\w]*$/, "");
    const textAfter = input.slice(cursorPos);
    const newValue = `${textBefore}@${user.username} ${textAfter}`;
    onSetInput(newValue);
    setMentionList([]);
    setMentionIndex(-1);
    setTimeout(() => inputRef.current.focus(), 0);
  };

  const handleKeyDown = (e) => {
    if (mentionList.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIndex((prev) => (prev + 1) % mentionList.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIndex(
          (prev) => (prev - 1 + mentionList.length) % mentionList.length
        );
      } else if (e.key === "Enter" && mentionIndex >= 0) {
        e.preventDefault();
        insertMention(mentionList[mentionIndex]);
        return;
      } else if (e.key === "Escape") {
        setMentionList([]);
        setMentionIndex(-1);
      }
    }

    if (e.key === "Enter" && !e.shiftKey && !mentionList.length) {
      e.preventDefault();
      handleSendWithFiles();
    }
  };

  const handleFileSelect = (e) => {
    if (isInputDisabled) return;

    const files = Array.from(e.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendWithFiles = () => {
    if (isInputDisabled) return;

    // Combine selected files and pasted images
    const allFiles = [...selectedFiles, ...pastedImages];

    if (allFiles.length > 0 || input.trim()) {
      onSend(allFiles.length > 0 ? allFiles : null);
      setSelectedFiles([]);
      // Pasted images will be cleared by parent component
    }
  };

  const getProfilePicUrl = (user) => {
    if (user?.profilePic) {
      const baseUrl = user.profilePic.startsWith("http")
        ? user.profilePic
        : `${BASE_DOWNLOAD_URL}${user.profilePic}`;
      return `${baseUrl}?v=${user.updatedAt || Date.now()}`;
    }
    return null;
  };

  const getUserAvatar = (user) => {
    const profilePicUrl = getProfilePicUrl(user);
    if (profilePicUrl) {
      return (
        <img
          src={profilePicUrl}
          alt={user.username}
          className="w-8 h-8 rounded-full object-cover"
        />
      );
    }
    const initials =
      `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() ||
      user.username?.[0]?.toUpperCase() ||
      "U";
    return (
      <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-bold text-gray-700">
        {initials}
      </div>
    );
  };

  const getReplyPreviewContent = (message) => {
    if (message.files && message.files.length > 0) {
      const file = message.files[0];
      if (file.type === "image") {
        return (
          <div className="flex items-center gap-2">
            <Image className="w-4 h-4" />
            <span>
              {message.files.length > 1
                ? `${message.files.length} Photos`
                : "Photo"}
            </span>
          </div>
        );
      } else if (file.type === "video") {
        return (
          <div className="flex items-center gap-2">
            <VideoCall className="w-4 h-4" />
            <span>
              {message.files.length > 1
                ? `${message.files.length} Videos`
                : "Video"}
            </span>
          </div>
        );
      } else if (file.type === "audio") {
        return (
          <div className="flex items-center gap-2">
            <MicIcon className="w-4 h-4" />
            <span>Voice message</span>
          </div>
        );
      } else {
        return (
          <div className="flex items-center gap-2">
            <Description className="w-4 h-4" />
            <span>
              {message.files.length > 1
                ? `${message.files.length} Files`
                : "Document"}
            </span>
          </div>
        );
      }
    }
    if (message.fileUrl) {
      if (message.fileType === "image") {
        return (
          <div className="flex items-center gap-2">
            <Image className="w-4 h-4" />
            <span>Photo</span>
          </div>
        );
      } else if (message.fileType === "video") {
        return (
          <div className="flex items-center gap-2">
            <VideoCall className="w-4 h-4" />
            <span>Video</span>
          </div>
        );
      } else if (message.fileType === "audio") {
        return (
          <div className="flex items-center gap-2">
            <MicIcon />
            <span>Voice message</span>
          </div>
        );
      } else {
        return (
          <div className="flex items-center gap-2">
            <Description className="w-4 h-4" />
            <span>Document</span>
          </div>
        );
      }
    }
    return message.content || "Message";
  };

  const handleSendVoice = (voiceFile) => {
    if (isInputDisabled) return;

    onSend([voiceFile]);
    setShowVoiceRecorder(false);
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith("image/")) return "🖼️";
    if (file.type.startsWith("video/")) return "🎥";
    if (file.type.startsWith("audio/")) return "🎵";
    return "📄";
  };

  return (
    <div className="bg-white border-t">
      {/* Inactive User Warning */}
      {isOtherUserInactive && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <span className="text-lg">🚫</span>
            <span className="font-medium">
              This user's account is inactive. You cannot send messages.
            </span>
          </div>
        </div>
      )}

      {/* Reply Preview Bar */}
      {replyingTo && (
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-l-4 border-l-blue-500">
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-blue-600 mb-1">
              Replying to {replyingTo.sender?.username || "Unknown"}
            </div>
            <div className="text-sm text-gray-600 truncate">
              {getReplyPreviewContent(replyingTo)}
            </div>
          </div>
          <button
            onClick={onCancelReply}
            className="ml-3 p-1 hover:bg-gray-200 rounded-full transition-colors"
            disabled={isInputDisabled}
          >
            <Close className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      )}

      {/* ✅ Pasted Images Preview */}
      {pastedImages.length > 0 && (
        <div className="px-4 py-3 bg-blue-50 border-b">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-blue-600">
              📋 {pastedImages.length} pasted image{pastedImages.length > 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {pastedImages.map((file, index) => (
              <div
                key={index}
                className="relative group bg-white rounded-lg border overflow-hidden"
              >
                <img
                  src={pastedImagePreviews[index]}
                  alt={`Pasted ${index + 1}`}
                  className="w-20 h-20 object-cover"
                />
                <button
                  onClick={() => onRemovePastedImage(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  disabled={isInputDisabled}
                >
                  <Close className="w-1 h-1" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 truncate">
                  {file.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Files Preview */}
      {selectedFiles.length > 0 && (
        <div className="px-4 py-3 bg-blue-50 border-b">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-blue-600">
              {selectedFiles.length} file{selectedFiles.length > 1 ? "s" : ""}{" "}
              selected
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border"
              >
                <span className="text-lg">{getFileIcon(file)}</span>
                <span className="text-sm truncate max-w-[150px]">
                  {file.name}
                </span>
                <button
                  onClick={() => removeFile(index)}
                  className="text-red-500 hover:text-red-700"
                  disabled={isInputDisabled}
                >
                  <Close className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Voice Recorder Mode */}
      {showVoiceRecorder ? (
        <div className="p-3">
          <VoiceRecorder
            onSendVoice={handleSendVoice}
            disabled={isInputDisabled}
          />
          <button
            onClick={() => setShowVoiceRecorder(false)}
            className="mt-2 text-sm text-gray-600 hover:text-gray-800"
            disabled={isInputDisabled}
          >
            Cancel
          </button>
        </div>
      ) : (
        <>
          {/* Main Input Area */}
          <div className="flex items-center p-3 gap-2 relative">
            <button
              onClick={onEmojiToggle}
              className="text-lg hover:bg-gray-100 p-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isInputDisabled}
              title={isOtherUserInactive ? "User is inactive" : "Add emoji"}
            >
              😊
            </button>

            {showEmoji && !isInputDisabled && (
              <div ref={pickerRef} className="absolute bottom-14 left-4 z-10">
                <EmojiPicker
                  onEmojiClick={(emoji) => {
                    onSetInput((v) => v + emoji.emoji);
                    inputRef.current?.focus();
                  }}
                  theme="light"
                />
              </div>
            )}

            <input
              type="file"
              id="fileInput"
              className="hidden"
              multiple
              onChange={handleFileSelect}
              disabled={isInputDisabled}
            />
            <label
              htmlFor="fileInput"
              className={`text-blue-600 text-lg px-2 hover:bg-gray-100 rounded transition-colors ${isInputDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                }`}
              title={isOtherUserInactive ? "User is inactive" : "Attach files"}
            >
              <AttachFile fontSize="small" />
            </label>

            {/* Text Input */}
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              disabled={isInputDisabled}
              placeholder={
                isOtherUserInactive
                  ? "Cannot send messages to inactive users..."
                  : "Type a message ..."
              }
              className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-blue-500 resize-none overflow-hidden min-h-[40px] max-h-[120px] disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
              rows={1}
              style={{
                height: "auto",
                minHeight: "40px",
                maxHeight: "120px",
                overflowY: input.split("\n").length > 3 ? "auto" : "hidden",
              }}
              onInput={(e) => {
                e.target.style.height = "auto";
                e.target.style.height =
                  Math.min(e.target.scrollHeight, 120) + "px";
              }}
            />

            {/* Mic and Send Buttons Side by Side */}
            <button
              onClick={() => setShowVoiceRecorder(true)}
              disabled={isInputDisabled}
              className="text-blue-600 p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              title={
                isOtherUserInactive
                  ? "User is inactive"
                  : "Record voice message"
              }
            >
              <MicIcon />
            </button>

            <button
              onClick={handleSendWithFiles}
              disabled={isInputDisabled || (!input.trim() && selectedFiles.length === 0 && pastedImages.length === 0)}
              className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center w-10 h-10 transition-all"
              title={isOtherUserInactive ? "User is inactive" : "Send message"}
            >
              {sending ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Send fontSize="small" />
              )}
            </button>
          </div>
        </>
      )}

      {/* Mention Dropdown */}
      {mentionList.length > 0 && !showVoiceRecorder && !isInputDisabled && (
        <div className="absolute bottom-16 left-12 bg-white border shadow-lg rounded-lg max-h-60 overflow-y-auto z-20 w-72">
          {mentionList.map((user, index) => (
            <div
              key={user._id}
              className={`px-3 py-2 cursor-pointer flex items-center gap-3 transition-colors ${index === mentionIndex ? "bg-gray-100" : "hover:bg-gray-50"
                }`}
              onClick={() => insertMention(user)}
            >
              {getUserAvatar(user)}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">
                  {user.username}
                </div>
                {(user.firstName || user.lastName) && (
                  <div className="text-xs text-gray-500 truncate">
                    {`${user.firstName || ""} ${user.lastName || ""}`.trim()}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}